import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useMenu } from '../../context/MenuContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const { FiMail, FiEye, FiCopy, FiDownload, FiSend, FiCheck, FiAlertCircle, FiRefreshCw, FiCalendar, FiEdit3, FiSave, FiExternalLink } = FiIcons;

const EmailNewsletter = () => {
  const { nextWeeklyMenus, fetchNextWeeklyMenus } = useMenu();
  const [emailHtml, setEmailHtml] = useState('');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [copySuccess, setCopySuccess] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isEditingIntro, setIsEditingIntro] = useState(false);
  const [customIntroText, setCustomIntroText] = useState({
    title: 'Liebe Geschäftspartner, Vereinsmitglieder und Gäste',
    subtitle: 'Entdecken Sie unser exklusives Wochenmenü-Angebot',
    content: 'Wir freuen uns, Ihnen unser hochwertiges Mittagsmenü für die kommende Woche präsentieren zu dürfen. Ob als Einzelperson, Geschäftspartner oder Vereinsmitglieder - bei uns sind alle herzlich willkommen und profitieren von unserem erstklassigen Service.'
  });

  // Lade die Menüs beim ersten Aufruf
  useEffect(() => {
    fetchNextWeeklyMenus();
  }, [fetchNextWeeklyMenus]);

  // Generiere automatisch HTML wenn Menüs verfügbar sind
  useEffect(() => {
    if (Object.keys(nextWeeklyMenus).length > 0) {
      generateEmailHtml();
    }
  }, [nextWeeklyMenus, customIntroText]);

  const refreshMenus = async () => {
    setRefreshing(true);
    try {
      await fetchNextWeeklyMenus();
    } catch (error) {
      console.error('Error refreshing menus:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const saveIntroText = () => {
    setIsEditingIntro(false);
    // Automatisch neu generieren wenn Intro-Text geändert wird
    if (Object.keys(nextWeeklyMenus).length > 0) {
      generateEmailHtml();
    }
  };

  const generateEmailHtml = () => {
    setIsGenerating(true);

    // Sortiere Menüs nach Wochentagen
    const sortedMenus = Object.values(nextWeeklyMenus).sort((a, b) => {
      const dayOrder = { 'dienstag': 1, 'mittwoch': 2, 'donnerstag': 3, 'freitag': 4 };
      return dayOrder[a.day] - dayOrder[b.day];
    });

    // Berechne Wochendaten
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const weekStart = format(nextWeek, 'd. MMMM', { locale: de });
    const weekEnd = format(new Date(nextWeek.getTime() + 4 * 24 * 60 * 60 * 1000), 'd. MMMM yyyy', { locale: de });

    const html = `<!DOCTYPE html>
<html lang="de" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="x-apple-disable-message-reformatting">
  <title>Wochenmenüs ${weekStart} - ${weekEnd}</title>
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:AllowPNG/>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
  <style>
    /* Reset styles */
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body, table, td, p, a, li, blockquote { -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    table, td { mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    img { -ms-interpolation-mode: bicubic; }
    
    /* Base styles */
    body {
      width: 100% !important;
      height: 100% !important;
      padding: 0 !important;
      margin: 0 !important;
      font-family: Arial, Helvetica, sans-serif;
      background-color: #f8f9fa;
    }
    table { border-collapse: collapse !important; }
    .email-container { max-width: 600px; margin: 0 auto; background-color: #ffffff; }
    
    .header {
      background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
      padding: 30px 20px;
      text-align: center;
    }
    .header h1 {
      color: #ffffff;
      font-size: 28px;
      font-weight: bold;
      margin: 0 0 10px 0;
      line-height: 1.2;
    }
    .header p { color: #fed7aa; font-size: 16px; margin: 0; }
    
    .intro-section {
      padding: 30px 20px;
      background-color: #ffffff;
      border-bottom: 1px solid #e5e7eb;
    }
    .intro-title {
      color: #1f2937;
      font-size: 24px;
      font-weight: bold;
      margin: 0 0 10px 0;
      line-height: 1.3;
    }
    .intro-subtitle {
      color: #f97316;
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 20px 0;
    }
    .intro-content {
      color: #6b7280;
      font-size: 16px;
      line-height: 1.6;
      margin: 0;
    }
    
    .content { padding: 40px 20px; }
    .section-title { text-align: center; margin-bottom: 40px; }
    .section-title h2 {
      color: #1f2937;
      font-size: 24px;
      margin-bottom: 15px;
      font-weight: bold;
    }
    .section-title p {
      color: #6b7280;
      font-size: 16px;
      line-height: 1.6;
      margin: 0;
    }
    
    .menu-item {
      margin-bottom: 30px;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      overflow: hidden;
      background-color: #ffffff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
    .menu-item:last-child { margin-bottom: 0; }
    
    .menu-header {
      background-color: #f97316;
      padding: 15px 20px;
      text-align: center;
    }
    .menu-day {
      color: #ffffff;
      font-size: 18px;
      font-weight: bold;
      margin: 0;
    }
    .menu-date {
      color: #fed7aa;
      font-size: 14px;
      margin: 5px 0 0 0;
    }
    
    .menu-body { padding: 0; }
    .menu-image {
      width: 100%;
      height: 200px;
      object-fit: cover;
      display: block;
    }
    
    .menu-info { padding: 20px; }
    .menu-title {
      color: #1f2937;
      font-size: 20px;
      font-weight: bold;
      margin: 0 0 10px 0;
      line-height: 1.3;
    }
    .menu-description {
      color: #6b7280;
      font-size: 14px;
      line-height: 1.6;
      margin: 0 0 15px 0;
    }
    .menu-price {
      color: #f97316;
      font-size: 24px;
      font-weight: bold;
      text-align: center;
      margin: 15px 0;
    }
    .menu-button {
      display: block;
      width: 100%;
      background-color: #f97316;
      color: #ffffff !important;
      text-decoration: none;
      padding: 15px 20px;
      border-radius: 8px;
      font-size: 16px;
      font-weight: bold;
      text-align: center;
      transition: background-color 0.3s;
      margin-top: 15px;
    }
    .menu-button:hover { background-color: #ea580c; }
    
    .cta-section {
      background-color: #f3f4f6;
      padding: 30px 20px;
      text-align: center;
      margin-top: 40px;
      border-radius: 12px;
    }
    .cta-title {
      color: #1f2937;
      font-size: 22px;
      font-weight: bold;
      margin: 0 0 15px 0;
    }
    .cta-text {
      color: #6b7280;
      font-size: 16px;
      line-height: 1.6;
      margin: 0 0 25px 0;
    }
    .cta-button {
      display: inline-block;
      background-color: #f97316;
      color: #ffffff !important;
      text-decoration: none;
      padding: 15px 30px;
      border-radius: 8px;
      font-size: 16px;
      font-weight: bold;
      margin: 0 10px 10px 0;
    }
    .cta-button:hover { background-color: #ea580c; }
    
    .benefits {
      margin: 30px 0;
      padding: 20px;
      background-color: #ecfdf5;
      border-radius: 8px;
      border-left: 4px solid #10b981;
    }
    .benefits h3 {
      color: #065f46;
      font-size: 18px;
      margin: 0 0 15px 0;
      font-weight: bold;
    }
    .benefits ul { margin: 0; padding-left: 20px; }
    .benefits li {
      color: #047857;
      font-size: 14px;
      line-height: 1.6;
      margin-bottom: 8px;
    }
    
    .business-info {
      margin: 30px 0;
      padding: 20px;
      background-color: #eff6ff;
      border-radius: 8px;
      border-left: 4px solid #3b82f6;
    }
    .business-info h3 {
      color: #1e40af;
      font-size: 18px;
      margin: 0 0 15px 0;
      font-weight: bold;
    }
    .business-info ul { margin: 0; padding-left: 20px; }
    .business-info li {
      color: #1e40af;
      font-size: 14px;
      line-height: 1.6;
      margin-bottom: 8px;
    }
    
    .location-info {
      margin: 30px 0;
      padding: 20px;
      background-color: #fef3c7;
      border-radius: 8px;
      border-left: 4px solid #f59e0b;
    }
    .location-info h3 {
      color: #92400e;
      font-size: 18px;
      margin: 0 0 15px 0;
      font-weight: bold;
    }
    .location-info ul { margin: 0; padding-left: 20px; }
    .location-info li {
      color: #b45309;
      font-size: 14px;
      line-height: 1.6;
      margin-bottom: 8px;
    }
    
    .footer {
      background-color: #1f2937;
      padding: 30px 20px;
      text-align: center;
    }
    .footer p {
      color: #9ca3af;
      font-size: 14px;
      line-height: 1.6;
      margin: 0 0 10px 0;
    }
    .footer a { color: #f97316; text-decoration: none; }
    
    /* Mobile responsiveness */
    @media screen and (max-width: 600px) {
      .email-container {
        width: 100% !important;
        max-width: 100% !important;
      }
      .header { padding: 20px 15px !important; }
      .header h1 { font-size: 24px !important; }
      .intro-section { padding: 20px 15px !important; }
      .intro-title { font-size: 20px !important; }
      .content { padding: 30px 15px !important; }
      .menu-info { padding: 15px !important; }
      .menu-title { font-size: 18px !important; }
      .cta-section { padding: 20px 15px !important; }
      .cta-button {
        display: block !important;
        margin: 0 0 10px 0 !important;
        width: 100% !important;
        box-sizing: border-box !important;
      }
    }
  </style>
</head>
<body>
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
    <tr>
      <td>
        <div class="email-container">
          <!-- Header -->
          <div class="header">
            <h1>Wöchentliches Mittagsmenü</h1>
            <p>Woche vom ${weekStart} bis ${weekEnd}</p>
          </div>

          <!-- Intro Section -->
          <div class="intro-section">
            <h2 class="intro-title">${customIntroText.title}</h2>
            <p class="intro-subtitle">${customIntroText.subtitle}</p>
            <p class="intro-content">${customIntroText.content}</p>
          </div>

          <!-- Content -->
          <div class="content">
            <div class="section-title">
              <h2>Unsere Menüs für die kommende Woche</h2>
              <p>Einzelanmeldungen oder Wochenbuchungen - Sie haben die Wahl!</p>
            </div>

            <!-- Location & Parking Info -->
            <div class="location-info">
              <h3>📍 Standort & Anfahrt:</h3>
              <ul>
                <li><strong>Adresse:</strong> Bahnhofstrasse 19, Au St. Gallen</li>
                <li><strong>Parkplätze:</strong> Direkt beim Bahnhof verfügbar</li>
                <li><strong>Öffentlicher Verkehr:</strong> Optimal mit Zug und Bus erreichbar</li>
                <li><strong>Servierzeit:</strong> Täglich 12:00 Uhr (individuelle Zeiten nach Absprache)</li>
              </ul>
            </div>

            <!-- Benefits Section -->
            <div class="benefits">
              <h3>🎁 Ihre Vorteile bei uns:</h3>
              <ul>
                <li><strong>Ab 4 Personen:</strong> Kostenlosen Kaffee für alle Teilnehmer</li>
                <li><strong>Ab 6 Personen:</strong> Kostenloses Dessert für die ganze Gruppe</li>
                <li><strong>Wochenanmeldung:</strong> Zusätzliches kostenloses Dessert</li>
                <li><strong>Einzelpersonen:</strong> Herzlich willkommen - auch alleine geniessen!</li>
              </ul>
            </div>

            <!-- Business Info Section -->
            <div class="business-info">
              <h3>🏢 Speziell für Firmen und Vereine:</h3>
              <ul>
                <li><strong>Gruppenrabatte:</strong> Attraktive Konditionen für grössere Teams</li>
                <li><strong>Flexible Zeiten:</strong> Individuelle Servierzeiten nach Absprache</li>
                <li><strong>Einfache Abrechnung:</strong> Sammelrechnung für Firmenkunden möglich</li>
                <li><strong>Regelmässige Buchungen:</strong> Wöchentliche Reservierung für Ihr Team</li>
                <li><strong>Besondere Wünsche:</strong> Vegetarische und diätetische Optionen verfügbar</li>
              </ul>
            </div>

            ${sortedMenus.map(menu => `
            <!-- Menu Item: ${menu.day} -->
            <div class="menu-item">
              <div class="menu-header">
                <h3 class="menu-day">${menu.day.charAt(0).toUpperCase() + menu.day.slice(1)}</h3>
                ${menu.formattedDate ? `<p class="menu-date">${menu.formattedDate}</p>` : ''}
              </div>
              <div class="menu-body">
                <img src="${menu.image}" alt="${menu.name}" class="menu-image">
                <div class="menu-info">
                  <h4 class="menu-title">${menu.name}</h4>
                  <p class="menu-description">${menu.description}</p>
                  <div class="menu-price">CHF ${menu.price.toFixed(2)}</div>
                  <a href="https://wochenmenu.hopp.sg/#/anmeldung/next-${menu.day}" class="menu-button">
                    🍽️ Für diesen Tag reservieren
                  </a>
                </div>
              </div>
            </div>
            `).join('')}

            <!-- Call to Action -->
            <div class="cta-section">
              <h3 class="cta-title">Jetzt anmelden und Platz sichern!</h3>
              <p class="cta-text">
                Reservieren Sie einzelne Tage oder sparen Sie mit unserer Wochenbuchung. 
                Auch Einzelpersonen sind herzlich willkommen!
              </p>
              <a href="https://wochenmenu.hopp.sg/#/naechste-woche" class="cta-button">
                🍽️ Alle Menüs ansehen
              </a>
              <a href="https://wochenmenu.hopp.sg/#/anmeldung/next-woche" class="cta-button">
                📅 Ganze Woche buchen
              </a>
            </div>

            <!-- Additional Info -->
            <div style="margin-top: 30px; padding: 20px; background-color: #f0f9ff; border-radius: 8px; border-left: 4px solid #0ea5e9;">
              <h3 style="color: #0c4a6e; font-size: 16px; margin: 0 0 10px 0;">ℹ️ Wichtige Informationen:</h3>
              <ul style="margin: 0; padding-left: 20px; color: #075985;">
                <li style="margin-bottom: 5px;"><strong>Anmeldeschluss:</strong> Täglich um 13:00 Uhr für den jeweiligen Tag</li>
                <li style="margin-bottom: 5px;"><strong>Standort:</strong> Bahnhofstrasse 19, Au St. Gallen</li>
                <li style="margin-bottom: 5px;"><strong>Parkplätze:</strong> Direkt beim Bahnhof verfügbar</li>
                <li style="margin-bottom: 5px;"><strong>Vorspeise:</strong> Wählbar zwischen Suppe oder Salat</li>
                <li style="margin-bottom: 5px;"><strong>Firmenanfragen:</strong> Gerne erstellen wir individuelle Angebote</li>
                <li><strong>Kontakt:</strong> Bei Fragen erreichen Sie uns unter news@hopp.sg</li>
              </ul>
            </div>
          </div>

          <!-- Footer -->
          <div class="footer">
            <p><strong>HOPP Mittagsmenü Service</strong></p>
            <p>Bahnhofstrasse 19, Au St. Gallen</p>
            <p>Täglich frische, regionale Küche für Ihr perfektes Mittagessen</p>
            <p>Ihr Partner für Firmen-, Vereins- und Einzelessen</p>
            <p>
              <a href="https://wochenmenu.hopp.sg/">Zur Website</a> | 
              <a href="mailto:news@hopp.sg">Kontakt</a> | 
              <a href="tel:+41786566575">+41 78 656 65 75</a>
            </p>
            <p style="font-size: 12px; margin-top: 15px;">
              Sie erhalten diese E-Mail als geschätzter Geschäftspartner, Vereinsmitglied oder interessierter Gast.<br>
              Falls Sie keine weiteren E-Mails wünschen, antworten Sie einfach mit "Abmelden".
            </p>
          </div>
        </div>
      </td>
    </tr>
  </table>
</body>
</html>`;

    setEmailHtml(html);
    setIsGenerating(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(emailHtml);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const downloadHtml = () => {
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const weekStart = format(nextWeek, 'yyyy-MM-dd', { locale: de });
    
    const blob = new Blob([emailHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wochenmenue-${weekStart}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // 🔥 VERBESSERTE FUNKTION: Erstelle .eml-Datei mit HTML-Content UND eingebetteten Bildern
  const downloadEmlFile = async () => {
    if (!emailHtml) return;

    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const weekStart = format(nextWeek, 'd. MMMM', { locale: de });
    const weekEnd = format(new Date(nextWeek.getTime() + 4 * 24 * 60 * 60 * 1000), 'd. MMMM yyyy', { locale: de });
    
    const subject = `Wochenmenüs ${weekStart} - ${weekEnd}`;
    const currentDate = new Date().toUTCString();

    // 🔥 NEUE FUNKTION: Konvertiere Bilder zu Base64 für Einbettung
    const convertImageToBase64 = async (imageUrl) => {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result.split(',')[1]; // Entferne "data:image/...;base64,"
            resolve(base64);
          };
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error converting image to base64:', error);
        return null;
      }
    };

    // 🔥 Sammle alle Menübilder und konvertiere sie zu Base64
    const sortedMenus = Object.values(nextWeeklyMenus).sort((a, b) => {
      const dayOrder = { 'dienstag': 1, 'mittwoch': 2, 'donnerstag': 3, 'freitag': 4 };
      return dayOrder[a.day] - dayOrder[b.day];
    });

    let boundary = `----=_NextPart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    let attachments = [];
    let modifiedHtml = emailHtml;

    // Konvertiere jedes Menübild zu Base64 und erstelle Attachments
    for (let i = 0; i < sortedMenus.length; i++) {
      const menu = sortedMenus[i];
      try {
        const base64Image = await convertImageToBase64(menu.image);
        if (base64Image) {
          const contentId = `menu_image_${i}`;
          // Ersetze die URL im HTML mit der Content-ID
          modifiedHtml = modifiedHtml.replace(
            new RegExp(menu.image.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
            `cid:${contentId}`
          );
          
          attachments.push({
            contentId: contentId,
            base64: base64Image,
            filename: `menu_${menu.day}.jpg`
          });
        }
      } catch (error) {
        console.error(`Error processing image for ${menu.day}:`, error);
      }
    }

    // 🔥 Erstelle .eml-Datei im MIME-Format mit eingebetteten Bildern
    let emlContent = `From: HOPP Mittagsmenü Service <news@hopp.sg>
To: 
Subject: ${subject}
Date: ${currentDate}
Message-ID: <${Date.now()}.hopp@newsletter>
MIME-Version: 1.0
Content-Type: multipart/related; boundary="${boundary}"

--${boundary}
Content-Type: text/html; charset=utf-8
Content-Transfer-Encoding: 8bit

${modifiedHtml}
`;

    // Füge alle Bild-Attachments hinzu
    attachments.forEach(attachment => {
      emlContent += `
--${boundary}
Content-Type: image/jpeg
Content-Transfer-Encoding: base64
Content-ID: <${attachment.contentId}>
Content-Disposition: inline; filename="${attachment.filename}"

${attachment.base64}
`;
    });

    emlContent += `\n--${boundary}--`;

    // Erstelle Blob und Download
    const blob = new Blob([emlContent], { type: 'message/rfc822' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wochenmenue-${format(nextWeek, 'yyyy-MM-dd')}.eml`;
    
    // Setze die richtigen Attribute für Mac Mail
    link.setAttribute('type', 'message/rfc822');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Zeige Hinweis
    setTimeout(() => {
      alert('Die .eml-Datei mit eingebetteten Bildern wurde heruntergeladen. Doppelklicken Sie auf die Datei, um sie in Mac Mail zu öffnen. Die E-Mail wird dann als HTML-Nachricht mit allen Bildern geöffnet und kann direkt versendet werden.');
    }, 500);
  };

  const hasMenus = Object.keys(nextWeeklyMenus).length > 0;

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">E-Mail Newsletter</h1>
          <p className="text-gray-600">Generieren Sie HTML-E-Mails für die Wochenmenüs</p>
        </div>
        <div className="flex space-x-3">
          <motion.button
            onClick={refreshMenus}
            disabled={refreshing}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`bg-gray-100 text-gray-700 px-4 py-2 rounded-xl font-semibold hover:bg-gray-200 transition-colors duration-300 flex items-center space-x-2 ${refreshing ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            <SafeIcon icon={FiRefreshCw} className={refreshing ? 'animate-spin' : ''} />
            <span>Menüs aktualisieren</span>
          </motion.button>
        </div>
      </div>

      {!hasMenus ? (
        <div className="bg-white rounded-2xl shadow-md p-8 text-center">
          <SafeIcon icon={FiAlertCircle} className="mx-auto text-4xl text-amber-500 mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Keine Menüs verfügbar</h3>
          <p className="text-gray-600 mb-6">
            Es sind noch keine Menüs für die nächste Woche verfügbar. Erstellen Sie zuerst Menüs im Bereich "Nächste Woche".
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.href = '#/admin/naechste-menus'}
            className="bg-primary-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors"
          >
            Menüs erstellen
          </motion.button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Controls */}
          <div className="lg:col-span-1 space-y-6">
            {/* Intro Text Editor */}
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Einleitungstext</h3>
                <motion.button
                  onClick={() => setIsEditingIntro(!isEditingIntro)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <SafeIcon icon={isEditingIntro ? FiSave : FiEdit3} />
                </motion.button>
              </div>

              {isEditingIntro ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                    <input
                      type="text"
                      value={customIntroText.title}
                      onChange={(e) => setCustomIntroText(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Untertitel</label>
                    <input
                      type="text"
                      value={customIntroText.subtitle}
                      onChange={(e) => setCustomIntroText(prev => ({ ...prev, subtitle: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Inhalt</label>
                    <textarea
                      value={customIntroText.content}
                      onChange={(e) => setCustomIntroText(prev => ({ ...prev, content: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <motion.button
                    onClick={saveIntroText}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-green-500 text-white py-2 rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiSave} />
                    <span>Speichern</span>
                  </motion.button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-800">{customIntroText.title}</h4>
                    <p className="text-sm text-primary-600">{customIntroText.subtitle}</p>
                    <p className="text-sm text-gray-600 mt-2">{customIntroText.content}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Action Controls */}
            <div className="bg-white rounded-2xl shadow-md p-6 sticky top-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">E-Mail Aktionen</h3>
              
              <div className="space-y-4">
                <motion.button
                  onClick={generateEmailHtml}
                  disabled={isGenerating}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-primary-500 text-white py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiMail} />
                  <span>{isGenerating ? 'Generiere...' : 'HTML generieren'}</span>
                </motion.button>

                {emailHtml && (
                  <>
                    {/* 🔥 VERBESSERTE .eml-Datei für Mac Mail mit eingebetteten Bildern */}
                    <motion.button
                      onClick={downloadEmlFile}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiExternalLink} />
                      <span>Für Mac Mail herunterladen (.eml)</span>
                    </motion.button>

                    <motion.button
                      onClick={copyToClipboard}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`w-full py-3 rounded-xl font-semibold transition-colors flex items-center justify-center space-x-2 ${
                        copySuccess 
                          ? 'bg-green-500 text-white' 
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      <SafeIcon icon={copySuccess ? FiCheck : FiCopy} />
                      <span>{copySuccess ? 'Kopiert!' : 'HTML kopieren'}</span>
                    </motion.button>

                    <motion.button
                      onClick={downloadHtml}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full bg-green-500 text-white py-3 rounded-xl font-semibold hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiDownload} />
                      <span>Als HTML downloaden</span>
                    </motion.button>
                  </>
                )}
              </div>

              {/* 🔥 VERBESSERTE Anleitung für Mac Mail */}
              {emailHtml && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">📧 Mac Mail Anleitung:</h4>
                  <div className="bg-blue-50 p-3 rounded-lg text-xs text-blue-800">
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Klicken Sie auf "Für Mac Mail herunterladen"</li>
                      <li>Doppelklicken Sie auf die .eml-Datei</li>
                      <li>Mac Mail öffnet die E-Mail automatisch als HTML</li>
                      <li>Alle Bilder sind bereits eingebettet</li>
                      <li>Fügen Sie Empfänger hinzu und senden Sie ab</li>
                    </ol>
                  </div>
                  <div className="mt-2 p-2 bg-green-50 rounded text-xs text-green-700">
                    ✅ <strong>NEU:</strong> Bilder werden automatisch in die E-Mail eingebettet - kein separater Upload nötig!
                  </div>
                </div>
              )}

              {emailHtml && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Vorschau-Modus</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setPreviewMode('desktop')}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        previewMode === 'desktop' 
                          ? 'bg-primary-500 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Desktop
                    </button>
                    <button
                      onClick={() => setPreviewMode('mobile')}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        previewMode === 'mobile' 
                          ? 'bg-primary-500 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Mobile
                    </button>
                  </div>
                </div>
              )}

              {/* Menü-Übersicht */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <SafeIcon icon={FiCalendar} className="mr-2" />
                  Verfügbare Menüs ({Object.keys(nextWeeklyMenus).length})
                </h4>
                <div className="space-y-2">
                  {Object.values(nextWeeklyMenus).map(menu => (
                    <div key={menu.id} className="flex items-center space-x-2 text-sm">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-gray-700">
                        {menu.day.charAt(0).toUpperCase() + menu.day.slice(1)}
                      </span>
                      <span className="text-gray-500 truncate">
                        {menu.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800 flex items-center">
                  <SafeIcon icon={FiEye} className="mr-2" />
                  E-Mail Vorschau
                </h3>
                <div className="text-sm text-gray-600">
                  {previewMode === 'mobile' ? '📱 Mobile Ansicht' : '🖥️ Desktop Ansicht'}
                </div>
              </div>

              {emailHtml ? (
                <div className={`p-4 ${previewMode === 'mobile' ? 'max-w-sm mx-auto' : ''}`}>
                  <div 
                    className="border border-gray-200 rounded-lg overflow-hidden bg-white"
                    style={{
                      width: previewMode === 'mobile' ? '320px' : '100%',
                      margin: previewMode === 'mobile' ? '0 auto' : '0'
                    }}
                  >
                    <iframe
                      srcDoc={emailHtml}
                      width="100%"
                      height="600"
                      className="border-0"
                      title="E-Mail Vorschau"
                    />
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <SafeIcon icon={FiMail} className="mx-auto text-4xl mb-4" />
                  <p>Generieren Sie zuerst eine E-Mail, um die Vorschau zu sehen.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* HTML Code Viewer (collapsible) */}
      {emailHtml && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8"
        >
          <details className="bg-white rounded-2xl shadow-md overflow-hidden">
            <summary className="px-6 py-4 bg-gray-50 cursor-pointer font-semibold text-gray-800 hover:bg-gray-100 transition-colors">
              HTML-Code anzeigen (zum Kopieren)
            </summary>
            <div className="p-6">
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-green-400 text-sm">
                  <code>{emailHtml}</code>
                </pre>
              </div>
              <div className="mt-4 flex space-x-3">
                <button
                  onClick={copyToClipboard}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2"
                >
                  <SafeIcon icon={copySuccess ? FiCheck : FiCopy} />
                  <span>{copySuccess ? 'Kopiert!' : 'Code kopieren'}</span>
                </button>
              </div>
            </div>
          </details>
        </motion.div>
      )}
    </div>
  );
};

export default EmailNewsletter;